import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { NavLink, LangToggle, AccountLink, CartButton, MobileMenu } from "./header/HeaderParts";

export const Header = () => {
  const { t, lang, setLang, setCartOpen, totals, pages, config } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMenu(false); }, [pathname]);

  const links = [
    { href: "/#products", id: "products", label: t.nav.products },
    { href: "/#manifesto", id: "manifesto", label: t.nav.story },
    { href: "/#ritual", id: "ritual", label: t.nav.ritual },
    ...pages.filter((p) => p.show_in_nav).map((p) => ({ href: `/p/${p.slug}`, id: `page-${p.slug}`, label: p.title[lang] || p.title.fr })),
    { href: "/#contact", id: "contact", label: t.nav.contact },
  ];
  const banner = config.announcement_enabled && (config.announcement?.[lang] || config.announcement?.fr);
  const compact = scrolled || menu;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${compact ? "backdrop-blur-xl bg-[#FAF7F2]/90 border-b border-stone-200/70 py-3" : "bg-transparent py-5"}`}
      data-testid="site-header"
    >
      {banner && (
        <div className={`bg-[#1C1917] text-[#EFEAE1] text-center text-[11px] font-mono uppercase tracking-[0.2em] py-2 px-4 mb-3 ${compact ? "-mt-3" : "-mt-5"}`} data-testid="announcement-banner">{banner}</div>
      )}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-baseline gap-2 group">
          <span className="font-serif text-2xl tracking-[0.28em] text-stone-900">NŪRA</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => <NavLink key={l.id} href={l.href} testId={`nav-${l.id}`}>{l.label}</NavLink>)}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <LangToggle lang={lang} setLang={setLang} />
          <AccountLink label={t.footer.account} />
          <CartButton label={t.cart} count={totals.count} onClick={() => setCartOpen(true)} />
          <button data-testid="mobile-menu-toggle" onClick={() => setMenu(!menu)} aria-label="Menu" className="md:hidden w-9 h-9 rounded-full border border-stone-300 flex items-center justify-center text-stone-700">
            {menu ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <MobileMenu open={menu} links={links} accountLabel={t.footer.account} onClose={() => setMenu(false)} />
    </motion.header>
  );
};
